<template>
	<div>
		<SectionHeader :title="t('common.location')" />

		<BaseMap
			ref="baseMapRef"
			height="400px"
			:initial-location="initialCoords"
			:plantings="plantings"
		>
			<template #controls="{ isInitialized }">
				<div v-if="isInitialized">
					<!-- Mode Selector - Top Left -->
					<div class="absolute left-3 top-3 z-10 flex flex-col gap-1">
						<button
							v-for="mode in modes"
							:key="mode.value"
							:class="[
								'flex h-11 w-11 items-center justify-center rounded-lg border-2 shadow-sm',
								currentMode === mode.value
									? 'border-blue-500 bg-blue-500 text-white'
									: 'border-gray-300 bg-white text-gray-600',
							]"
							@click="changeMode(mode.value)"
						>
							<ion-icon :icon="mode.icon" class="text-xl" />
						</button>
					</div>

					<!-- Clear/Reset button - Top Right -->
					<div v-if="showClear" class="absolute right-3 top-20 z-10">
						<button
							class="flex items-center justify-center rounded-lg border-2 border-red-500 bg-red-500 px-3 py-2 text-sm font-medium text-white shadow-sm"
							@click="clearDrawings"
						>
							{{
								currentMode === "draw_polygon"
									? t("common.clear")
									: t("common.reset")
							}}
						</button>
					</div>
				</div>
			</template>
		</BaseMap>

		<!-- Overlap Notification -->
		<div
			v-if="
				overlappingUnselectedPlantings.length > 0 && !hideIntersections
			"
			class="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3"
		>
			<div class="flex items-start">
				<ion-icon
					:icon="warningOutline"
					class="mr-2 mt-0.5 flex-shrink-0 text-blue-500"
				/>
				<div class="text-sm">
					<p class="font-medium text-blue-800">
						{{ t("note.active-planting-auto-detection") }}
					</p>
					<p class="text-blue-700">
						{{
							t("note.drawn-area-overlaps", {
								count: overlappingUnselectedPlantings.length,
							})
						}}
					</p>
					<div class="mt-2 flex flex-wrap gap-1">
						<span
							v-for="p in overlappingUnselectedPlantings"
							:key="p.id"
							class="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800"
						>
							{{ p.name }}
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, watch, computed, Ref } from "vue";
	import BaseMap from "@/components/ui/maps/BaseMap.vue";
	import {
		centerOfMass,
		booleanPointInPolygon,
		point,
		booleanIntersects,
		polygon,
	} from "@turf/turf";
	import SectionHeader from "@/components/feature/notes/SectionHeader.vue";
	import { useDefaultPlantingsQuery } from "@/composables/useDefaultPlantingsQuery";
	import { useDrawingLayer } from "@/components/ui/maps/useDrawingLayer";
	import { IonIcon } from "@ionic/vue";
	import {
		locationOutline,
		shapesOutline,
		closeOutline,
		warningOutline,
	} from "ionicons/icons";
	import { useFeatureFlags } from "seedgreen-shared/queries/featureFlags";
	import { FEATURE_FLAGS } from "seedgreen-shared/types/featureFlags.types";
	import { useI18n } from "vue-i18n";

	const { t } = useI18n();

	const coordinates = defineModel<number[][] | null>({
		required: false,
		default: null,
	});

	const props = defineProps<{
		selectedPlantingId: number | null;
	}>();

	// Feature flag check
	const { featureFlags } = useFeatureFlags();
	const hideIntersections = computed(() => {
		return !(
			featureFlags.value?.includes(FEATURE_FLAGS.NoteIntersections) ??
			false
		);
	});

	// Mode configuration
	const modes = [
		{ value: "draw_point" as const, icon: locationOutline },
		{ value: "draw_polygon" as const, icon: shapesOutline },
		{ value: "none" as const, icon: closeOutline },
	];

	const { plantings } = useDefaultPlantingsQuery();

	// BaseMap reference
	const baseMapRef = ref<InstanceType<typeof BaseMap> | null>(null);
	const map = computed(() => baseMapRef.value?.map || null);

	// Computed properties for planting data
	const planting = computed(() =>
		props.selectedPlantingId
			? plantings.value?.find((p) => p.id === props.selectedPlantingId)
			: null,
	);

	const initialCoords = computed(() => {
		if (!planting.value?.latestBoundary) return undefined;

		const coords = centerOfMass(planting.value.latestBoundary);
		return coords.geometry.coordinates as [number, number];
	});

	const {
		currentFeatures,
		currentMode,
		showClear,
		clearDrawings,
		changeMode,
		finalizePolygonIfDrawing,
	} = useDrawingLayer(map as Ref<mapboxgl.Map | null>, {
		initialCoordinates: initialCoords,
	});

	const extractedCoordinates = computed(() => {
		if (currentFeatures.value.length === 0) return null;

		if (currentMode.value === "draw_point") {
			const pointFeatures = currentFeatures.value.filter(
				(f) => f.geometry.type === "Point",
			);
			const latestPoint = pointFeatures[pointFeatures.length - 1];
			return latestPoint?.geometry.type === "Point"
				? [latestPoint.geometry.coordinates]
				: null;
		}

		if (currentMode.value === "draw_polygon") {
			const polygon = currentFeatures.value.find(
				(f) => f.geometry.type === "Polygon",
			);
			return polygon?.geometry.type === "Polygon"
				? polygon.geometry.coordinates[0]
				: null;
		}

		return null;
	});

	// Sync with model
	watch(
		extractedCoordinates,
		(newCoords) => {
			coordinates.value = newCoords;
		},
		{ immediate: true },
	);

	const overlappingUnselectedPlantings = computed(() => {
		if (!coordinates.value || coordinates.value.length === 0) return [];

		if (
			currentMode.value !== "draw_polygon" &&
			currentMode.value !== "draw_point"
		) {
			return [];
		}

		const overlappingPlantings = plantings.value?.filter((p) => {
			if (props.selectedPlantingId === p.id) return false;
			if (!p.latestBoundary) return false;

			// Only include active plantings (Active or Established status)
			if (!p.isLive) return false;

			if (currentMode.value === "draw_point") {
				if (!coordinates.value || coordinates.value.length === 0)
					return false;
				const pointFeature = point(coordinates.value[0]);
				return booleanPointInPolygon(pointFeature, p.latestBoundary);
			} else if (currentMode.value === "draw_polygon") {
				const coordsArray = Array.isArray(coordinates.value)
					? coordinates.value
					: [];
				// Only proceed if valid polygon ring
				if (coordsArray.length < 4) return false;
				const drawnPoly = polygon([coordsArray]);
				return booleanIntersects(drawnPoly, p.latestBoundary);
			}
			return false;
		});

		return overlappingPlantings || [];
	});

	defineExpose({ finalizePolygonIfDrawing });
</script>
